import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import InfoIcon from "@material-ui/icons/Info";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { useNotify, Title } from "react-admin";
import { ThemeProvider } from "@material-ui/styles";
import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import { apiUrl } from "../../dataProvider";
import customTheme from "../../customTheme";
import Loader from "../Loader";

const PASSWORD_PATTERN = /^(?=.*[A-Z])(?=.*\d).{8,}$/;
const PASSWORD_HINT =
  "Password must have at least 8 characters with one uppercase letter and one number";

const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
    maxWidth: 420,
    margin: "auto",
  },
  labelWithInfo: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  infoIconWrap: {
    display: "inline-flex",
    alignItems: "center",
    lineHeight: 0,
  },
  infoIcon: {
    fontSize: 16,
  },
  errorText: {
    color: "#f44336",
    fontSize: 12,
    marginTop: 4,
  },
  passwordAdornment: {
    position: "relative",
    right: "auto",
    marginRight: 4,
  },
  passwordToggle: {
    padding: 8,
  },
}));

const PasswordField = React.forwardRef((props, ref) => {
  const classes = useStyles();
  const [showPassword, setShowPassword] = useState(false);

  return (
    <TextField
      {...props}
      inputRef={ref}
      type={showPassword ? "text" : "password"}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end" className={classes.passwordAdornment}>
            <IconButton
              aria-label={showPassword ? "Hide password" : "Show password"}
              onClick={() => setShowPassword((prev) => !prev)}
              className={classes.passwordToggle}
              size="small"
            >
              {showPassword ? (
                <VisibilityOff fontSize="small" />
              ) : (
                <Visibility fontSize="small" />
              )}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
});

const ChangePassword = () => {
  const classes = useStyles();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const { control, errors, handleSubmit, getValues, reset } = useForm({});

  const submit = async ({ password }) => {
    setLoading(true);
    try {
      const response = await axios.put(`${apiUrl}/users/change-password`, {
        password,
      });
      notify(response?.data?.message || "Password updated successfully", { type: "success" });
      reset();
    } catch (err) {
      const status = err?.response?.status;
      const responseData = err?.response?.data;
      const message =
        responseData?.message ||
        responseData?.password ||
        (Array.isArray(responseData?.password) ? responseData.password[0] : null) ||
        err?.message ||
        "Failed to update password";

      if (status === 401 || status === 403) {
        window.location.replace("/#/login");
        return;
      }

      notify(message, { type: "warning" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Title title="Change Password" />
      <div className={classes.root}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom className={classes.labelWithInfo}>
              Change Password
              <Tooltip title={PASSWORD_HINT} arrow leaveDelay={200}>
                <span className={classes.infoIconWrap}>
                  <InfoIcon
                    htmlColor={customTheme.palette.secondary.dark}
                    className={classes.infoIcon}
                  />
                </span>
              </Tooltip>
            </Typography>
            <form onSubmit={(e) => e.preventDefault()}>
              <Controller
                name="password"
                control={control}
                rules={{
                  required: "Password is required",
                  validate: (value) =>
                    PASSWORD_PATTERN.test(value) || PASSWORD_HINT,
                }}
                as={
                  <PasswordField
                    label="New Password"
                    fullWidth
                    margin="normal"
                  />
                }
              />
              {errors.password && (
                <Typography className={classes.errorText}>
                  {errors.password.message}
                </Typography>
              )}

              <Controller
                name="confirmPassword"
                control={control}
                rules={{
                  required: "Please confirm your password",
                  validate: (value) =>
                    value === getValues().password || "The passwords do not match",
                }}
                as={
                  <PasswordField
                    label="Confirm Password"
                    fullWidth
                    margin="normal"
                  />
                }
              />
              {errors.confirmPassword && (
                <Typography className={classes.errorText}>
                  {errors.confirmPassword.message}
                </Typography>
              )}

              <CardActions>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={loading}
                  onClick={handleSubmit(submit)}
                >
                  Update Password
                </Button>
              </CardActions>
              <Loader timedOut={loading} />
            </form>
          </CardContent>
        </Card>
      </div>
    </ThemeProvider>
  );
};

export {
  PasswordField,
  PASSWORD_PATTERN,
  PASSWORD_HINT,
};

export default ChangePassword;
